// src/context/AuthContext.js
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';
import axios from 'axios';

// Käytä REACT_APP_API_URL:ia. Poista trailing slash.
// Jos env puuttuu -> palataan suhteelliseen polkuun (rewrites/proxy).
const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
const apiUrl = (path) => (API_BASE ? `${API_BASE}${path}` : path);

// Nimetty export + default export lopussa
export const AuthContext = createContext(null);

const STORAGE_KEY = 'user';
const normalizeToken = (t) =>
  t ? String(t).replace(/^Bearer\s+/i, '').trim() : '';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);        // { ...profile, token }
  const [isReady, setIsReady] = useState(false); // true kun localStorage luettu

  // --- Helpers -------------------------------------------------------------
  const saveSession = useCallback((u) => {
    setUser(u || null);
    if (u) localStorage.setItem(STORAGE_KEY, JSON.stringify(u));
    else localStorage.removeItem(STORAGE_KEY);
  }, []);

  const clearSession = useCallback(() => {
    setUser(null);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const getAuthHeaders = useCallback(() => {
    const token = user?.token ? normalizeToken(user.token) : '';
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [user?.token]);

  // --- Hydraus localStoragesta --------------------------------------------
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        const token = normalizeToken(
          parsed?.token ?? parsed?.accessToken ?? parsed?.jwt ?? null
        );
        const normalized = token ? { ...parsed, token } : parsed;
        setUser(normalized || null);
      }
    } catch {
      localStorage.removeItem(STORAGE_KEY);
      setUser(null);
    } finally {
      setIsReady(true);
    }
  }, []);

  // --- Axios-interceptorit (auth-header + 401→logout) ----------------------
  useEffect(() => {
    const reqId = axios.interceptors.request.use((config) => {
      const hasAuth = !!config.headers?.Authorization;
      const token = user?.token ? normalizeToken(user.token) : '';
      if (!hasAuth && token) {
        config.headers = {
          ...(config.headers || {}),
          Authorization: `Bearer ${token}`,
        };
      }
      return config;
    });

    const resId = axios.interceptors.response.use(
      (resp) => resp,
      (error) => {
        if (error?.response?.status === 401) clearSession();
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(reqId);
      axios.interceptors.response.eject(resId);
    };
  }, [user?.token, clearSession]);

  // --- Toiminnot -----------------------------------------------------------
  const login = useCallback(
    async (email, password) => {
      // Backendissasi login = POST /api/users/login
      const url = apiUrl('/api/users/login');
      const { data } = await axios.post(url, { email, password });

      const token = normalizeToken(
        data?.token || data?.accessToken || data?.jwt
      );
      const profile = data?.user || data?.profile || data?.data || {};

      if (!token) throw new Error('Kirjautuminen epäonnistui: token puuttuu vastauksesta.');

      const merged = { ...profile, token };
      saveSession(merged);
      return merged;
    },
    [saveSession]
  );

  const logout = useCallback(() => {
    clearSession();
    // (Valinnainen) axios.post(apiUrl('/api/users/logout')).catch(() => {});
  }, [clearSession]);

  const updateUser = useCallback((patch) => {
    setUser((prev) => {
      const base = prev || {};
      const next = {
        ...base,
        ...(typeof patch === 'function' ? patch(base) : patch),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  // --- Johdetut arvot ------------------------------------------------------
  const isAuthenticated = !!user?.token;

  const value = useMemo(
    () => ({
      user,
      isReady,
      isAuthenticated,
      login,
      logout,
      updateUser,
      saveSession,
      clearSession,
      getAuthHeaders,
      token: user?.token || null, // yhteensopivuus
      API_BASE,                   // saatavilla tarvittaessa
      apiUrl,                     // saatavilla tarvittaessa
    }),
    [
      user,
      isReady,
      isAuthenticated,
      login,
      logout,
      updateUser,
      saveSession,
      clearSession,
      getAuthHeaders,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Helppo hook
export const useAuth = () => useContext(AuthContext);

// Default export myös (taaksepäinyhteensopivuus)
export default AuthContext;
